import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Box,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LanguageIcon from '@material-ui/icons/Language';
import LockIcon from '@material-ui/icons/Lock';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';

interface IngressLink {
  host: string;
  path: string;
  tls: boolean;
  env: string;
  envColor: string;
}

interface EnvConfig {
  key: string;
  displayName: string;
  color: string;
  namespace: string;
}

const useStyles = makeStyles(theme => ({
  list: {
    padding: 0,
  },
  listItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' },
  },
  path: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  envChip: {
    height: 18,
    fontSize: '0.6rem',
    fontWeight: 600,
    color: '#fff',
    marginLeft: theme.spacing(1),
  },
  tlsIcon: {
    fontSize: 14,
    color: '#4caf50',
    marginLeft: 4,
  },
}));

export const IngressLinksCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const annotations = entity.metadata.annotations ?? {};
  const argoPrefix = annotations['fondative.io/argocd-app-prefix'];
  const k8sId = annotations['backstage.io/kubernetes-id'] ?? entity.metadata.name;

  const [links, setLinks] = useState<IngressLink[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let envs: EnvConfig[] = [];
    try {
      const arr = configApi.getConfigArray('fondative.environments');
      envs = arr.map(c => ({
        key: c.getString('key'),
        displayName: c.getString('displayName'),
        color: c.getOptionalString('color') ?? '#1976d2',
        namespace: c.getOptionalString('namespace') ?? 'default',
      }));
    } catch {
      setLoading(false);
      return;
    }

    const allLinks: IngressLink[] = [];
    let pending = envs.length;

    envs.forEach(env => {
      fetchApi
        .fetch(
          `${backendBaseUrl}/api/proxy/kubernetes/api/apis/networking.k8s.io/v1/namespaces/${env.namespace}/ingresses`,
        )
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
          if (!data?.items) return;

          // Match ingresses by:
          // 1. ArgoCD instance label (argocd.argoproj.io/instance == {argoPrefix}-{envKey})
          // 2. Ingress name contains k8s-id keywords
          const argoInstance = argoPrefix ? `${argoPrefix}-${env.key}` : '';

          for (const ingress of data.items) {
            const labels = ingress.metadata?.labels ?? {};

            // Match by ArgoCD instance label
            const matchesArgo = argoInstance && labels['argocd.argoproj.io/instance'] === argoInstance;
            if (!matchesArgo) continue;

            const tlsHosts = new Set(
              (ingress.spec?.tls ?? []).flatMap((t: any) => t.hosts ?? []),
            );

            for (const rule of ingress.spec?.rules ?? []) {
              const host = rule.host;
              if (!host) continue;
              for (const path of rule.http?.paths ?? [{ path: '/' }]) {
                allLinks.push({
                  host,
                  path: path.path ?? '/',
                  tls: tlsHosts.has(host),
                  env: env.displayName,
                  envColor: env.color,
                });
              }
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          pending--;
          if (pending === 0) {
            // Deduplicate by host
            const seen = new Set<string>();
            const deduped = allLinks.filter(l => {
              const key = `${l.host}${l.path}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            setLinks(deduped);
            setLoading(false);
          }
        });
    });
  }, [configApi, fetchApi, backendBaseUrl, k8sId, argoPrefix]);

  if (loading) {
    return (
      <Card>
        <CardHeader title="URLs" />
        <CardContent>
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={20} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!links.length) return null;

  return (
    <Card>
      <CardHeader title="URLs" />
      <CardContent style={{ paddingTop: 0 }}>
        <List className={classes.list}>
          {links.map(link => {
            const protocol = link.tls ? 'https' : 'http';
            const url = `${protocol}://${link.host}${link.path === '/' ? '' : link.path}`;
            return (
              <ListItem key={url} className={classes.listItem} disableGutters>
                <ListItemIcon style={{ minWidth: 32 }}>
                  <LanguageIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  primary={
                    <Box display="flex" alignItems="center">
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.link}
                      >
                        {link.host}
                      </a>
                      {link.tls && (
                        <LockIcon className={classes.tlsIcon} />
                      )}
                      <Chip
                        size="small"
                        label={link.env}
                        className={classes.envChip}
                        style={{ backgroundColor: link.envColor }}
                      />
                    </Box>
                  }
                  secondary={
                    link.path !== '/' ? (
                      <Typography className={classes.path}>{link.path}</Typography>
                    ) : undefined
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
};
